import { generateComponentClasses, generateElementSelectors } from '../styles/selectors'
import Attrs from '../models/Attrs'
import type { Flow, FlowPage, MediaImage } from '../models/types'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../helpers/templating'
import { addLanguageController, getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--media-image'

export default function renderMediaImage(
  component: MediaImage,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  userData: { [key: string]: unknown }
): string {
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  const componentClasses = generateComponentClasses({
    component,
    page,
    builderVersion,
  })

  const componentSrc = replaceStorageURLsFromFirebaseToCloudflare(component.src) || ''
  const src =
    replaceStorageURLsFromFirebaseToCloudflare(renderTemplate(componentSrc, userData)) || ''

  attrs
    .addController(CONTROLLER)
    .addDOMClass(componentClasses, 'MediaImage')
    .addValueIfExists(CONTROLLER, 'src', src)

  const captionAttrs = new Attrs(attrs.static_render).addDOMClass(
    'caption',
    generateElementSelectors({
      elementType: 'Caption',
      component,
      page,
      builderVersion,
    })
  )

  //* Add language controller for the caption
  addLanguageController({ attrs: captionAttrs, component, keys: ['caption'] })

  if (component.page_reactive_classes) captionAttrs.addReactiveElement()
  const caption = component.caption ? `<div ${captionAttrs}>${component.caption}</div>` : ''
  const imgAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      ...generateElementSelectors({
        elementType: 'Image',
        component,
        page,
        builderVersion,
      })
    )
    .addTarget(CONTROLLER, 'image')
    .add('src', src)

  //* Add language controller for the src and alt_text
  addLanguageController({ attrs: imgAttrs, component, keys: ['src', 'alt_text'] })
  
  //* Add template controller if the src has {{
  if (component.src && component.src.includes('{{'))
    imgAttrs
      .addController('template')
      .addValue('template', 'target', 'src')
      .addValueIfExists('template', 'text', encodeURIComponent(componentSrc || ''))
      .addValueIfExists('template', 'always-rerender', component.always_rerender)

  if (component.page_reactive_classes) imgAttrs.addReactiveElement()
  if (component.alt_text) imgAttrs.add('alt', component.alt_text)

  return `<div ${attrs}><img ${imgAttrs}/>${caption}</div>`
}
